import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";
import { MANAGER_COLORS, type ManagerColor } from "@salesboard/contracts";
import { useT } from "@/i18n";

/** The radio group needs a value for "unassigned"; `null` is not one a `RadioItem` can carry. */
const NONE = "__none__";

/**
 * Pick a colour from the one curated palette, or clear it.
 *
 * Extracted from the Users page's colour cell (2026-08-25) once stages needed the same control
 * — a second swatch popover would have drifted from the first in exactly the ways that matter:
 * whether the options are named, whether "None" is offered, and whether re-picking the current
 * value writes an audit row saying nothing.
 *
 * Every swatch is **labelled by name**, never colour alone: a picker whose only distinction is
 * its colour is unusable by the reader the palette already does least for. Selecting the value
 * that is already selected is a no-op, because every consumer of this audits the change.
 */
export function ColourPicker({
  value,
  onChoose,
  label,
  triggerLabel,
  warning,
  note,
  disabled = false,
  id,
}: {
  value: ManagerColor | null;
  onChoose: (color: ManagerColor | null) => void;
  /** Names the control for a screen reader — "Colour for Petr Dvořák", not just "Colour". */
  label: string;
  /** What the closed trigger reads, usually the chosen colour's own name. */
  triggerLabel: string;
  /** Shown inside the popover: "another manager already has this", and the like. */
  warning?: string;
  /** A quiet explanation under the options, for the state where nothing is assigned yet. */
  note?: string;
  disabled?: boolean;
  id?: string;
}): React.JSX.Element {
  const t = useT();
  const warningId = warning ? `${id ?? "colour"}-warning` : undefined;
  const noteId = note ? `${id ?? "colour"}-note` : undefined;
  const describedBy = [warningId, noteId].filter(Boolean).join(" ") || undefined;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        id={id}
        disabled={disabled}
        className="border-border hover:bg-accent focus-visible:ring-ring flex h-8 w-32 items-center gap-2 rounded-md border px-2 text-xs focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
      >
        {/* The accessible name carries the row *and* the value: "Colour" repeated down a table
            names nothing, and an `aria-label` would hide the value from a reader. */}
        {/* The label brings its own punctuation — the catalogue keys end in ": ", because
            where a colon goes is a language's business, not this component's. */}
        <span className="sr-only">{label}</span>
        <Swatch color={value} />
        {triggerLabel}
        <ChevronDown className="ml-auto h-3 w-3 shrink-0 opacity-60" aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          aria-describedby={describedBy}
          className="bg-popover text-popover-foreground z-50 min-w-48 rounded-md border p-1 shadow-md"
        >
          {warning && (
            // A note inside `role="menu"` is not an item and a reader walks past it, which is
            // why the menu names it as its description above as well.
            <DropdownMenu.Label id={warningId} className="text-warning px-2 py-1.5 text-xs">
              {warning}
            </DropdownMenu.Label>
          )}

          <DropdownMenu.RadioGroup
            value={value ?? NONE}
            onValueChange={(next) => {
              if (next === (value ?? NONE)) return;
              onChoose(next === NONE ? null : (next as ManagerColor));
            }}
          >
            {MANAGER_COLORS.map((color) => (
              <DropdownMenu.RadioItem
                key={color.key}
                value={color.key}
                className="hover:bg-accent data-[highlighted]:bg-accent data-[state=checked]:font-medium flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
              >
                <Swatch color={color.key} />
                {t(`colour.${color.key}`)}
              </DropdownMenu.RadioItem>
            ))}

            <DropdownMenu.Separator className="bg-border my-1 h-px" />

            <DropdownMenu.RadioItem
              value={NONE}
              className="hover:bg-accent data-[highlighted]:bg-accent data-[state=checked]:font-medium flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none"
            >
              <Swatch color={null} />
              {t("colour.none")}
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>

          {note && (
            <DropdownMenu.Label id={noteId} className="text-muted-foreground max-w-48 px-2 py-1.5 text-xs">
              {note}
            </DropdownMenu.Label>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function Swatch({ color }: { color: ManagerColor | null }): React.JSX.Element {
  return (
    <span
      aria-hidden
      className={
        color === null
          ? "border-border block h-3.5 w-3.5 shrink-0 rounded-full border border-dashed"
          : "border-border block h-3.5 w-3.5 shrink-0 rounded-full border"
      }
      style={color === null ? undefined : { backgroundColor: `var(--manager-${color})` }}
    />
  );
}
